import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, CheckCircle, ChevronLeft, ChevronRight, PenTool,
  Type, Upload, X, Download, ZoomIn, ZoomOut, Loader2
} from 'lucide-react';

interface SignatureField {
  id: string;
  type: 'signature' | 'initials' | 'date' | 'text';
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  required: boolean;
  value?: string;
}

interface DocumentData {
  id: string;
  name: string;
  pages: number;
  fields: SignatureField[];
}

export function SignDocument() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [fields, setFields] = useState<SignatureField[]>([]);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type' | 'upload'>('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    loadDocument();
  }, [documentId]);

  const loadDocument = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setDocument({
      id: documentId || '1',
      name: 'Warranty Deed - 123 Main St',
      pages: 3,
      fields: [
        {
          id: '1',
          type: 'signature',
          page: 3,
          x: 100,
          y: 400,
          width: 200,
          height: 50,
          label: 'Grantor Signature',
          required: true,
        },
        {
          id: '2',
          type: 'date',
          page: 3,
          x: 350,
          y: 400,
          width: 120,
          height: 30,
          label: 'Date',
          required: true,
        },
        {
          id: '3',
          type: 'initials',
          page: 1,
          x: 500,
          y: 700,
          width: 60,
          height: 30,
          label: 'Initials',
          required: true,
        },
      ],
    });
    
    setFields([
      {
        id: '1',
        type: 'signature',
        page: 3,
        x: 100,
        y: 400,
        width: 200,
        height: 50,
        label: 'Grantor Signature',
        required: true,
      },
      {
        id: '2',
        type: 'date',
        page: 3,
        x: 350,
        y: 400,
        width: 120,
        height: 30,
        label: 'Date',
        required: true,
        value: new Date().toLocaleDateString(),
      },
      {
        id: '3',
        type: 'initials',
        page: 1,
        x: 500,
        y: 700,
        width: 60,
        height: 30,
        label: 'Initials',
        required: true,
      },
    ]);
    
    setLoading(false);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1f2e';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handleCanvasMouseUp = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const applySignature = () => {
    if (!activeField) return;
    
    let signatureData = '';
    
    if (signatureMode === 'draw') {
      const canvas = canvasRef.current;
      if (canvas) {
        signatureData = canvas.toDataURL();
      }
    } else if (signatureMode === 'type') {
      signatureData = typedSignature;
    }
    
    setFields(prev => prev.map(f => 
      f.id === activeField ? { ...f, value: signatureData } : f
    ));
    
    setActiveField(null);
    clearCanvas();
    setTypedSignature('');
  };

  const handleSubmit = async () => {
    const incompleteFields = fields.filter(f => f.required && !f.value);
    if (incompleteFields.length > 0) {
      alert(`Please complete all required fields: ${incompleteFields.map(f => f.label).join(', ')}`);
      return;
    }
    
    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setSubmitting(false);
    setCompleted(true);
  };

  const currentPageFields = fields.filter(f => f.page === currentPage);
  const completedCount = fields.filter(f => f.value).length;
  const progress = (completedCount / fields.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading document...</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg"
        >
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Document Signed!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for signing "{document?.name}". All parties will receive a copy once everyone has signed.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/documents')}
              className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
            >
              View Documents
            </button>
            <button
              className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="font-semibold text-slate-900">{document?.name}</h1>
              <p className="text-sm text-slate-500">Please review and sign below</p>
            </div>
          </div>
          
          {/* Progress */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm text-slate-600">{completedCount} of {fields.length} fields</span>
              <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={completedCount < fields.length || submitting}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Finish Signing
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Document Viewer */}
        <div className="flex-1 p-4 overflow-auto">
          <div className="max-w-3xl mx-auto">
            {/* Zoom controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setZoom(Math.max(50, zoom - 25))}
                  className="p-2 hover:bg-white rounded-lg"
                >
                  <ZoomOut className="w-4 h-4 text-slate-600" />
                </button>
                <span className="text-sm text-slate-600">{zoom}%</span>
                <button
                  onClick={() => setZoom(Math.min(200, zoom + 25))}
                  className="p-2 hover:bg-white rounded-lg"
                >
                  <ZoomIn className="w-4 h-4 text-slate-600" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 hover:bg-white rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 text-slate-600" />
                </button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {document?.pages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(document?.pages || 1, currentPage + 1))}
                  disabled={currentPage === document?.pages}
                  className="p-2 hover:bg-white rounded-lg disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>

            {/* Document page */}
            <div 
              className="bg-white rounded-lg shadow-lg relative"
              style={{ 
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                minHeight: 800,
              }}
            >
              {/* Placeholder for PDF rendering */}
              <div className="p-8 text-slate-400 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Document page {currentPage}</p>
                <p className="text-sm">(PDF viewer would render here)</p>
              </div>

              {/* Signature fields overlay */}
              {currentPageFields.map((field) => (
                <div
                  key={field.id}
                  onClick={() => !field.value && setActiveField(field.id)}
                  className={`absolute border-2 rounded cursor-pointer transition-all ${
                    field.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-orange-500 bg-orange-50 hover:bg-orange-100 border-dashed'
                  }`}
                  style={{
                    left: field.x,
                    top: field.y,
                    width: field.width,
                    height: field.height,
                  }}
                >
                  {field.value ? (
                    <div className="w-full h-full flex items-center justify-center text-green-700">
                      {field.type === 'signature' && field.value.startsWith('data:') ? (
                        <img src={field.value} alt="Signature" className="max-w-full max-h-full" />
                      ) : (
                        <span className="font-signature text-lg">{field.value}</span>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-orange-600 text-xs">
                      <PenTool className="w-3 h-3 mr-1" />
                      {field.label}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fields sidebar */}
        <div className="w-80 bg-white border-l border-slate-200 p-4 overflow-auto">
          <h3 className="font-semibold text-slate-900 mb-4">Fields to Complete</h3>
          <div className="space-y-2">
            {fields.map((field) => (
              <button
                key={field.id}
                onClick={() => {
                  setCurrentPage(field.page);
                  if (!field.value) setActiveField(field.id);
                }}
                className={`w-full p-3 rounded-lg text-left transition-colors ${
                  field.value
                    ? 'bg-green-50 border border-green-200'
                    : activeField === field.id
                    ? 'bg-orange-100 border border-orange-300'
                    : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {field.value ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <PenTool className="w-4 h-4 text-orange-600" />
                    )}
                    <span className={field.value ? 'text-green-700' : 'text-slate-700'}>
                      {field.label}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">Page {field.page}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {activeField && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-lg w-full"
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">
                {fields.find(f => f.id === activeField)?.label}
              </h3>
              <button
                onClick={() => setActiveField(null)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-4">
              {/* Mode tabs */}
              <div className="flex gap-2 mb-4">
                {[
                  { mode: 'draw', label: 'Draw', icon: PenTool },
                  { mode: 'type', label: 'Type', icon: Type },
                  { mode: 'upload', label: 'Upload', icon: Upload },
                ].map(({ mode, label, icon: Icon }) => (
                  <button
                    key={mode}
                    onClick={() => setSignatureMode(mode as any)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium transition-colors ${
                      signatureMode === mode
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Signature input */}
              <div className="border-2 border-slate-200 rounded-lg p-4 mb-4">
                {signatureMode === 'draw' && (
                  <div>
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={150}
                      className="w-full border border-slate-100 rounded bg-slate-50 cursor-crosshair"
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseUp={handleCanvasMouseUp}
                      onMouseLeave={handleCanvasMouseUp}
                    />
                    <button
                      onClick={clearCanvas}
                      className="mt-2 text-sm text-slate-500 hover:text-slate-700"
                    >
                      Clear
                    </button>
                  </div>
                )}

                {signatureMode === 'type' && (
                  <div>
                    <input
                      type="text"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      placeholder="Type your name"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg text-2xl font-signature text-center focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-slate-400 text-center mt-2">
                      Your typed name will be used as your signature
                    </p>
                  </div>
                )}

                {signatureMode === 'upload' && (
                  <div className="text-center py-8">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-600 mb-2">Upload signature image</p>
                    <input type="file" accept="image/*" className="hidden" id="sig-upload" />
                    <label
                      htmlFor="sig-upload"
                      className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg cursor-pointer hover:bg-slate-200"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>

              {/* Legal text */}
              <p className="text-xs text-slate-500 mb-4">
                By clicking "Apply Signature", you agree that this electronic signature is the legal equivalent of your handwritten signature.
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setActiveField(null)}
                  className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={applySignature}
                  className="flex-1 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                >
                  Apply Signature
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
